import React from 'react';
import { Waves, Mail, Phone, MapPin } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-slate-900 to-slate-800 text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl translate-x-48 translate-y-48"></div>

      <div className="container mx-auto px-4 pt-16 pb-8 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Waves className="w-7 h-7 text-white" />
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Good-Bloom
              </span>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
              Pionnier de la dépollution aquatique éco-intelligente, Good-Bloom développe des solutions autonomes pour purifier les plans d'eau et restaurer les écosystèmes aquatiques.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-300">
                <Mail className="w-5 h-5 text-teal-400" />
                <a href={`mailto:${mockData.contact.email}`} className="hover:text-teal-400 transition-colors duration-200">
                  {mockData.contact.email}
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Phone className="w-5 h-5 text-teal-400" />
                <a href={`tel:${mockData.contact.phone}`} className="hover:text-teal-400 transition-colors duration-200">
                  {mockData.contact.phone}
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <MapPin className="w-5 h-5 text-teal-400" />
                <span>{mockData.contact.address}</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Navigation</h3>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('accueil')}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200 text-left"
                >
                  Accueil
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('technologies')}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200 text-left"
                >
                  Technologies
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('realisations')}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200 text-left"
                >
                  Réalisations
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('rendez-vous')}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200 text-left"
                >
                  Rendez-vous
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-300 hover:text-teal-400 transition-colors duration-200 text-left"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Nos Services</h3>
            <ul className="space-y-3 text-gray-300">
              <li>Filtration Plastiques</li>
              <li>Traitement Nutriments</li>
              <li>Absorption Hydrocarbures</li>
              <li>Systèmes Autonomes</li>
              <li>Monitoring Continu</li>
              <li>Maintenance Préventive</li>
            </ul>
          </div>
        </div>

        {/* Social Media & Newsletter */}
        <div className="border-t border-gray-700 pt-8 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div>
              <h4 className="text-lg font-semibold mb-3 text-white">Suivez-nous</h4>
              <div className="flex space-x-4">
                <a 
                  href={mockData.contact.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center text-white transition-all duration-200 transform hover:scale-110"
                >
                  <span className="text-sm font-bold">in</span>
                </a>
                <a 
                  href={mockData.contact.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-red-600 hover:bg-red-700 rounded-xl flex items-center justify-center text-white transition-all duration-200 transform hover:scale-110"
                >
                  <span className="text-sm font-bold">YT</span>
                </a>
              </div>
            </div>

            <div className="text-center md:text-right">
              <h4 className="text-lg font-semibold mb-3 text-white">Innovation Écologique</h4>
              <div className="flex items-center space-x-2 text-gray-300">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm">Certifié ISO 14001 - Solutions Durables</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-gray-400 text-sm">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
            <span>© {currentYear} Good-Bloom. Tous droits réservés.</span>
            <div className="flex space-x-4">
              <a href="#" className="hover:text-teal-400 transition-colors duration-200">
                Mentions légales
              </a>
              <a href="#" className="hover:text-teal-400 transition-colors duration-200">
                Politique de confidentialité
              </a>
              <a href="#" className="hover:text-teal-400 transition-colors duration-200">
                RGPD
              </a>
            </div>
          </div>
          <div className="text-center md:text-right">
            <span className="text-gray-500">
              Site conçu avec 💚 pour l'environnement
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;